/** A single pixel position in a facility's floor plan image, in that image's own pixel space. */
export interface FloorplanPixelPoint {
  x: number;
  y: number;
}

/**
 * A deployment area's calibrated world-to-pixel transform with the matrix
 * reshaped into a 2D array for computation. This is the internal format used
 * by transformation functions after reshaping the flat array from the API.
 */
export interface WorldToPixelTransformAsset {
  sourceFrame: string;
  targetFrame: string;
  matrix: number[][];
}

/**
 * Reshapes a flat 9-element matrix array (row-major order) into a 3x3 nested array.
 * The API returns matrices as flat arrays: [a, b, c, d, e, f, g, h, i]
 * This reshapes them into: [[a, b, c], [d, e, f], [g, h, i]]
 */
export function reshapeMatrix(flatMatrix: number[]): number[][] {
  if (flatMatrix.length !== 9) {
    throw new Error(`Expected 9 matrix elements, got ${flatMatrix.length}`);
  }
  return [
    [flatMatrix[0], flatMatrix[1], flatMatrix[2]],
    [flatMatrix[3], flatMatrix[4], flatMatrix[5]],
    [flatMatrix[6], flatMatrix[7], flatMatrix[8]],
  ];
}

/**
 * A deployment area's calibrated world-to-pixel transform: a 3x3 homogeneous
 * matrix between the area's world coordinate reference frame and its floor
 * plan image's pixel space, as returned by `GET /v1/areas/{areaId}/map`'s
 * `floorplan.worldToPixelTransform` (see `useAreaMap`/`useFacilitySchematicAssets`).
 */
export interface WorldToPixelTransformAsset {
  sourceFrame: string;
  targetFrame: string;
  matrix: number[][];
}

/**
 * Applies a world-to-pixel affine transform (a 3x3 homogeneous matrix, see
 * `WorldToPixelTransformAsset`) to a single UTM 30N world coordinate
 * (`source_frame: "utm_30n_world"`), returning its position in the
 * transform's pixel target frame (the floor plan image's own native pixel
 * space).
 *
 * The full perspective divide by `w` is applied even though the current matrix's
 * bottom row is `[0, 0, 1]` (a pure affine transform), so this keeps working
 * unchanged if the matrix is ever regenerated as a true projective transform.
 *
 * There is currently only one calibrated mapping per deployment, so callers
 * pass its transform directly rather than looking one up by a
 * deployment/reference-frame id. Revisit if multiple mappings per deployment
 * need to be supported.
 */
export function worldToFloorplanPixel(
  transform: Pick<WorldToPixelTransformAsset, "matrix">,
  worldX: number,
  worldY: number
): FloorplanPixelPoint {
  const [row0, row1, row2] = transform.matrix;

  const px = row0[0] * worldX + row0[1] * worldY + row0[2];
  const py = row1[0] * worldX + row1[1] * worldY + row1[2];
  const w = row2[0] * worldX + row2[1] * worldY + row2[2];

  return { x: px / w, y: py / w };
}

/** A single world coordinate in the transform's source reference frame (e.g. UTM 30N). */
export interface WorldPoint {
  x: number;
  y: number;
}

/**
 * Applies the inverse of a world-to-pixel transform's 3x3 homogeneous matrix,
 * mapping a floor plan pixel position back to its source world coordinate
 * (`source_frame`, e.g. `"utm_30n_world"`).
 *
 * Used only for the dev-only cursor coordinates overlay, to read off a world
 * coordinate for a point picked directly on the schematic.
 */
export function floorplanPixelToWorld(
  transform: Pick<WorldToPixelTransformAsset, "matrix">,
  pixelX: number,
  pixelY: number
): WorldPoint {
  const inverse = invert3x3(transform.matrix);
  const [row0, row1, row2] = inverse;

  const wx = row0[0] * pixelX + row0[1] * pixelY + row0[2];
  const wy = row1[0] * pixelX + row1[1] * pixelY + row1[2];
  const w = row2[0] * pixelX + row2[1] * pixelY + row2[2];

  return { x: wx / w, y: wy / w };
}

/** Inverts a 3x3 matrix (given as an array of rows) via its adjugate and determinant. */
function invert3x3(matrix: number[][]): number[][] {
  const [[a, b, c], [d, e, f], [g, h, i]] = matrix;

  const determinant = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);

  const cofactors = [
    [e * i - f * h, c * h - b * i, b * f - c * e],
    [f * g - d * i, a * i - c * g, c * d - a * f],
    [d * h - e * g, b * g - a * h, a * e - b * d],
  ];

  return cofactors.map((row) => row.map((value) => value / determinant));
}
